// ---------------------------------------------------------------------------
// ModelingProgressBandV3 — estilos locais.
// ---------------------------------------------------------------------------
//
// Faixa unica que substitui (1) os 3 cards do topo de "Todos os
// inputs / Seus inputs / Status por responsavel" e (2) o trecho do
// painel "monthlyStatusDiv" que mostrava o chip de status do mes +
// botao de Dashboard.
//
// Layout:
//   - Compacto (padrao) ........... titulo, status do mes, botao do
//                                   drawer de progresso, barra de 4
//                                   segmentos e contador X/Y.
//   - Expandido (toggle chevron) .. tres cards classicos com donut +
//                                   tiles de filtro / lista de
//                                   responsaveis.
//
// O ``clientColor`` (cor do tenant) e injetado via props de makeStyles
// porque o material-dashboard-pro nao expoe esse token no theme — o
// padrao mais comum no codebase e' chamar ``useThemeColors()`` na view
// e injetar o que for dinamico.
// ---------------------------------------------------------------------------

import { makeStyles } from "@material-ui/core/styles";
import {
  blackColor,
  blendColors,
  dangerColor,
  grayColor,
  successColor,
  warningColor,
  whiteColor,
} from "../../../../assets/jss/material-dashboard-pro-react";

// Tons que espelham os usados no ``DashedDonut`` (fonte canonica das
// cores por estado canonico do input). ``PROCESSING_GRAY`` foi removido
// junto com as classes ``seg*`` da barra — a barra agora le a cor
// inline de ``INPUT_STATE_BACKGROUNDS``.
const IN_PROGRESS_GREEN = "#a5d6a7";

const useBandStyles = makeStyles((theme) => ({
  // -------------------------------------------------------------------------
  // Estrutura
  // -------------------------------------------------------------------------
  bandCard: {
    margin: 0,
    marginTop: "8px",
    marginBottom: 0,
  },
  // Mesmo interior que ``bandCard`` + ``CardBody``, dentro do Card
  // pai da view (lista de inputs) — sem sombra/card extra.
  bandEmbeddedWrap: {
    margin: 0,
    marginTop: 0,
    borderTop: `1px solid ${grayColor[8]}`,
  },
  bandBody: {
    padding: "0 !important",
  },
  // Linha compacta (sempre visivel). Padding generoso lateral porque
  // o titulo + bloco de status do lado direito comem espaco e a barra
  // logo abaixo ja respira no eixo Y.
  compactWrap: {
    padding: "6px 4px",
    display: "flex",
    flexDirection: "column",
  },
  // PA-022: linha unica do compacto — [mes] [contador] [barra] [chevron].
  compactLine: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: "32px",
  },
  // Wrapper da barra que cresce no espaco restante (entre o
  // contador e o chevron). ``min-width: 0`` para nao ultrapassar o
  // container quando o contador vira "1234/5678".
  compactBarWrap: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  // ---------------------------------------------------------------------
  // PA-022 (mai/2026): classes deprecadas — mantidas por enquanto
  // para nao quebrar consumidores remanescentes / storybook.
  //   ``topLine`` / ``headLeft`` / ``headRight`` / ``legendText``
  //   / ``activeActorsText`` / ``drawerBtn`` / ``bottomLine`` /
  //   ``bottomLineMetrics``.
  // ---------------------------------------------------------------------
  topLine: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: "30px",
  },
  // Linha inferior do compacto: só margem aqui; o fade na transição de mês
  // aplica-se apenas em ``bottomLineMetrics`` (contador + barra).
  bottomLine: {
    marginTop: "2px",
    marginBottom: "-2px",
  },
  bottomLineMetrics: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: 0,
  },
  // Tipografia "legenda": espelha exatamente o ``title`` usado pelo
  // ``InputsLegend`` (mouseover da visao detalhada e cabecalho da
  // visao geral). Mesma fonte e' aplicada nos titulos dos 3 cards
  // do expandido.
  legendText: {
    fontSize: "10px",
    color: grayColor[1],
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  // Bloco esquerdo do compacto: [mes] [titulo] [chip status]. Tudo
  // alinhado e separado por gap pequeno para a leitura encadeada.
  headLeft: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: 0,
  },
  headRight: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  monthTag: {
    color: grayColor[2],
    fontSize: "11px",
    fontWeight: 500,
    padding: "0 9px",
    borderRadius: "999px",
    background: grayColor[19],
    border: "1px solid " + grayColor[8],
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
  },
  // Onda 1.8c: texto contextual ao lado do badge de status do mes
  // ("Lendo Venta Bruta...", "Cruzando Venta x Ajustes...",
  // "Calculando PnL Logistica..."). Visualmente discreto: italic +
  // cor mais suave que o legendText. ``flex: 1`` + ``min-width: 0``
  // + ``ellipsis`` para que textos longos (varios atores) cortem
  // graciosamente em vez de empurrar o botao "Calculos" para fora
  // da linha.
  activeActorsText: {
    color: grayColor[1],
    fontSize: "11px",
    fontStyle: "italic",
    fontWeight: 400,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: "0 1 auto",
    maxWidth: "320px",
  },
  // Botao "Detalhes" — espelha o modelo "nao-selecionado" dos botoes
  // de visao do header da view (``viewButton`` + ``simple``): fundo
  // branco, texto na cor do cliente, sombra suave. Quando o drawer
  // de progresso esta aberto, viramos para o modelo "selecionado"
  // (fundo cliente + texto branco) — mesma logica de ``simple``
  // alternando para ``color``.
  drawerBtn: (props) => ({
    margin: 0,
    padding: "4px 8px",
    borderRadius: "4px",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: "inherit",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    cursor: "pointer",
    boxShadow: "0 0 2px rgba(0,0,0,0.3)",
    background: whiteColor,
    color: props.clientColor,
    transition:
      "background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      // background: blendColors(props.clientColor, whiteColor, 0.06),
      // boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
    },
    "&.active": {
      background: props.clientColor,
      color: theme.palette.custom?.clientTextColor || whiteColor,
      boxShadow:
        "0 0 2px rgba(0,0,0,0.3), 0 2px 6px " +
        blendColors(props.clientColor, whiteColor, 0.5),
    },
    "&.disabled": {
      opacity: 0.4,
      pointerEvents: "none",
    },
    "& svg": {
      fontSize: "16px",
    },
  }),
  chevronBtn: {
    width: "32px",
    height: "32px",
    borderRadius: "4px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[0],
    cursor: "pointer",
    transition: "background-color 0.15s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
    "& svg": {
      transition: "transform 0.2s ease",
    },
    "&.open svg": {
      transform: "rotate(180deg)",
    },
  },
  counter: {
    color: grayColor[2],
    fontSize: "16px",
    lineHeight: 1.25,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    textAlign: "left",
    padding: "0 2px",
    "& small": {
      color: grayColor[1],
      fontWeight: 500,
      fontSize: "12px",
    },
  },

  // -------------------------------------------------------------------------
  // Barra de progresso ("quadradinhos")
  // -------------------------------------------------------------------------
  //
  // Cada input do mes vira UM "quadradinho" colorido pelo seu estado
  // canonico. O efeito visual e' analogo ao do donut do expandido: voce
  // ve UMA pecinha por input. A separacao entre eles e' feita pelo
  // ``gap: 2px`` que deixa transparecer o fundo branco do trilho.
  //
  // A barra e' informativa (nao clicavel) — os filtros agora vivem
  // exclusivamente nos tiles do modo expandido. Mantemos tooltips por
  // grupo de cor, posicionados bem proximos da barra.
  bar: {
    flex: 1,
    display: "flex",
    gap: "2px",
    height: "14px",
    minWidth: "120px",
    borderRadius: "999px",
    background: whiteColor,
    boxShadow: "inset 0 1px 1px rgba(0,0,0,0.06)",
    overflow: "hidden",
    position: "relative",
  },
  // Wrapper de um "grupo de cor" (bucket). Carrega o tooltip e contem
  // N quadradinhos do mesmo bucket separados pelo mesmo gap branco.
  barGroup: {
    display: "flex",
    gap: "1px",
    minWidth: 0,
    height: "100%",
  },
  // Quadradinho individual — 1 por input. A cor de fundo agora vem
  // INLINE direto de ``INPUT_STATE_BACKGROUNDS`` (em
  // ``services/inputsV3StateRules.js``) — mesma fonte usada pelo pip
  // do ``InputBoxV3``, garantindo que a barra reflita exatamente a
  // cor de cada card. Por isso as antigas classes ``seg*`` foram
  // removidas: elas duplicavam a paleta e divergiam (ex.: bucket
  // vermelho da barra vs dot laranja do tile do expandido).
  barCell: {
    flex: 1,
    minWidth: "2px",
    height: "100%",
  },

  // -------------------------------------------------------------------------
  // Faixa expandida (3 cards lado a lado, dentro do mesmo Card)
  // -------------------------------------------------------------------------
  expanded: {
    // Padding uniforme nas 4 direcoes (antes o top estava zerado).
    padding: "8px",
    background: blendColors(grayColor[19], whiteColor, 0.6),
    borderTop: "1px solid " + grayColor[8],
    borderRadius: "6px",
  },
  expandedHead: {
    color: grayColor[0],
    fontSize: "12px",
    fontWeight: 500,
    marginBottom: "8px",
    paddingTop: "10px",
    paddingLeft: "4px",
  },
  innerCard: {
    margin: 0,
    height: "100%",
  },
  innerBody: {
    padding: "12px 12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  // Mesmo estilo de fonte do titulo do compacto e do
  // ``InputsLegend.title`` — "legenda" uppercase 10px.
  innerTitle: {
    fontSize: "10px",
    color: grayColor[1],
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  // Layout do conteudo dos 2 primeiros cards: donut a esquerda,
  // tiles empilhados a direita.
  cardSplit: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    gap: "14px",
    alignItems: "center",
  },
  donutHolder: {
    position: "relative",
    width: "92px",
    height: "92px",
    cursor: "help",
  },
  donutOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  donutNum: {
    fontSize: "20px",
    fontWeight: 700,
    color: blackColor,
    lineHeight: 1.1,
    letterSpacing: "0.2px",
  },
  donutDen: {
    color: grayColor[1],
    fontWeight: 500,
    fontSize: "14px",
  },
  donutCap: {
    marginTop: "2px",
    fontSize: "9px",
    color: grayColor[1],
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    fontWeight: 500,
  },

  // -------------------------------------------------------------------------
  // Tiles de filtro empilhados (uma "linha" por estado)
  // -------------------------------------------------------------------------
  tileStack: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    minWidth: 0,
  },
  tile: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 10px",
    border: "1px solid " + grayColor[8],
    background: whiteColor,
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "12px",
    color: grayColor[2],
    textAlign: "left",
    transition: "all 0.15s ease",
    boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
    appearance: "none",
    width: "100%",
    "&:hover": {
      background: grayColor[19],
      borderColor: grayColor[5],
      transform: "translateX(2px)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    // Estado "filtro aplicado": neutro / cinza (ao inves da cor do
    // tenant) — o destaque vem do contorno mais firme + leve elevacao,
    // nao da paleta do cliente. Sem barrinha lateral.
    "&.active": {
      background: grayColor[15],
      borderColor: grayColor[3],
      // boxShadow: `0 0 0 1px ${grayColor[3]}, 0 2px 6px rgba(0,0,0,0.08)`,
      transform: "translateX(2px)",
      fontWeight: 500,
    },
    "&.disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
      "&:hover": {
        background: whiteColor,
        transform: "none",
        boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
        borderColor: grayColor[8],
      },
    },
    // tile informativo (Entregues) — sem filtro real, so leitura
    "&.read-only": {
      cursor: "default",
      "&:hover": {
        background: whiteColor,
        transform: "none",
        boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
        borderColor: grayColor[8],
      },
    },
  },
  tileDot: {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
    flexShrink: 0,
    boxShadow: "0 0 0 2px rgba(0,0,0,0.04)",
  },
  tileDotDelivered: { background: successColor[2] },
  tileDotInProgress: { background: IN_PROGRESS_GREEN },
  tileDotAttention: { background: warningColor[1] },
  // I-11 (Onda 1.3 — convergencia visual): ``pending_decision`` agora
  // pinta o dot do tile na MESMA laranja escura (#ed6c02) que ja e'
  // usada no ``segPendingDecision`` (cells da barra) e em
  // ``inputsV3StateRules.js`` (badge "ajuste necessario"). Antes
  // estava como dangerColor[2] (vermelho), gerando conflito visual:
  // a barra mostrava cell laranja, mas o dot do tile do mesmo bucket
  // mostrava vermelho — usuario via "2 cores" para o mesmo estado.
  // Mantemos um tile separado de critical (vermelho) porque a soma
  // narrativa ainda agrupa pending_decision junto com critical no
  // contador "n itens precisam de acao".
  tileDotPendingDecision: { background: "#ed6c02" },
  tileDotCritical: { background: dangerColor[2] },
  tileDotNotSent: { background: grayColor[5] },
  tileLabel: {
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tileLabelMute: {
    color: grayColor[1],
    fontSize: "10px",
    fontStyle: "italic",
    marginLeft: "4px",
  },
  tileCount: {
    fontWeight: 700,
    color: blackColor,
    fontSize: "13px",
    minWidth: "26px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  // Quando o tile esta filtrando, escurecemos um pouco o contador
  // (cinza chumbo) sem usar a cor do cliente.
  tileCountActive: {
    color: grayColor[1],
  },

  // -------------------------------------------------------------------------
  // 3o card: lista de responsaveis (clicavel como filtro)
  // -------------------------------------------------------------------------
  // O 3o card usa o mesmo ``innerTitle`` no JSX; este atalho fica
  // disponivel caso queiramos um label extra na lista.
  respHead: {
    fontSize: "10px",
    color: grayColor[1],
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: "6px",
  },
  respScroll: {
    flex: "1 1 0",
    minHeight: 0,
    maxHeight: "172px",
    paddingRight: "2px",
    marginRight: "-2px",
    overflowY: "auto",
    overscrollBehavior: "contain",
  },
  respEmpty: {
    flex: "1 1 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[1],
    fontStyle: "italic",
    fontSize: "12px",
  },
  respRow: (props) => ({
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    gap: "10px",
    padding: "3px 6px",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      background: grayColor[19],
    },
    "&.selected": {
      background: blendColors(props.clientColor, whiteColor, 0.12),
      "&:hover": {
        background: blendColors(props.clientColor, whiteColor, 0.18),
      },
    },
  }),
  respName: {
    flex: 1,
    color: grayColor[2],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  respNameEmpty: {
    color: grayColor[1],
    fontStyle: "italic",
  },
  respNameMe: {
    fontWeight: 500,
  },
  respBadges: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },
  respTiny: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: grayColor[2],
    fontWeight: 600,
    minWidth: "22px",
    justifyContent: "flex-end",
  },
  respTinyDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  respCheck: (props) => ({
    width: "14px",
    height: "14px",
    borderRadius: "3px",
    border: "1.5px solid " + grayColor[5],
    background: whiteColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&.checked": {
      borderColor: props.clientColor,
      background: props.clientColor,
    },
  }),
}));

export default useBandStyles;
